// 导入相关工具和组件
import axios from 'axios';
import { ElMessage, ElLoading, ElMessageBox } from 'element-plus';
import { useAuthStore } from '@/store/modules/auth';
import router from '@/router';
import { pinia } from '@/store';

// 在文件级别创建authStore实例，使其在整个文件中可访问
const authStore = useAuthStore(pinia);

// 创建axios实例
// 根据环境变量决定是否使用代理模式
const baseURL = process.env.VUE_APP_PROXY === 'true' ? '/api' : process.env.VUE_APP_API_BASE_URL;

const service = axios.create({
  baseURL: baseURL,  // 根据配置使用代理或直接URL
  timeout: 30000, // 增加超时时间到30秒，解决AI请求超时问题
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
});

console.log('Request baseURL:', baseURL);
console.log('是否使用代理模式:', process.env.VUE_APP_PROXY === 'true');
console.log('API基础URL:', process.env.VUE_APP_API_BASE_URL);

// 请求加载动画
let loadingInstance = null;

// 请求拦截器增强日志
service.interceptors.request.use(
  (config) => {
    // 显示加载动画
    if (config.showLoading !== false) {
      loadingInstance = ElLoading.service({
        lock: true,
        text: '加载中...',
        background: 'rgba(0, 0, 0, 0.7)'
      });
    }

    // 添加token - 为所有非登录请求添加token
    if (config.url !== '/login' && config.url !== '/auth/login') {
      const token = authStore.token;
      if (token) {
        // 直接使用token，不添加Bearer前缀，因为后端期望的格式是token_teacherId_timestamp
        config.headers.Authorization = token;
        console.log('添加Token到请求:', token.substring(0, 10) + '...');
        // 为学生请求添加额外日志
        if (config.url.includes('/student/') || config.url.includes('/student/app/')) {
          console.log('学生请求 - URL:', config.url);
          console.log('当前用户角色:', authStore.role);
        }
      } else {
        console.log('没有Token，不添加Authorization头');
        // 如果是学生请求且没有token，尝试提供模拟学生token用于开发测试
          if ((config.url.includes('/student/') || config.url.includes('/student/app/')) && process.env.NODE_ENV === 'development') {
            console.log('为学生请求提供模拟token');
            const mockToken = 'token_601_1234567890'; // 模拟学生token，使用数字ID 601
            config.headers.Authorization = mockToken;
          }
      }
    }

    // 添加请求日志，特别是添加学生的请求
    if (config.url === '/teacher/students' && config.method === 'post') {
      console.log('发送添加学生请求:', config.data);
      console.log('请求URL:', config.url);
      console.log('请求方法:', config.method);
      console.log('请求头:', config.headers);
    }
    
    // 对所有学生管理相关的请求添加日志
    if (config.url.includes('/teacher/students')) {
      console.log(`学生管理请求 - URL: ${config.url}, 方法: ${config.method}`);
    }

    return config;
  },
  (error) => {
    // 关闭加载动画
    if (loadingInstance) loadingInstance.close();
    console.error('请求参数错误');
    return Promise.reject(error);
  }
);

// 响应拦截器增强日志
service.interceptors.response.use(
  (response) => {
    // 关闭加载动画
    if (loadingInstance) loadingInstance.close();

    // 登录请求现在直接发送到后端数据库处理，不再使用前端模拟数据
    // 保留登录请求的日志记录
    if (response.config.url === '/api/login' && response.config.method === 'post') {
      console.log('登录请求发送到后端数据库处理:', response.config.data);
    }

    // 添加响应日志，特别是添加学生的响应
    if (response.config.url === '/teacher/students' && response.config.method === 'post') {
      console.log('添加学生完整响应:', response.data);
      console.log('响应状态码:', response.status);
      console.log('响应头:', response.headers);
    }

    // 对所有学生管理相关的响应添加日志
    if (response.config.url.includes('/teacher/students')) {
      console.log(`学生管理响应 - URL: ${response.config.url}, 状态码: ${response.status}`);
      console.log('响应数据:', response.data);
    }

    // 添加成绩录入相关响应的详细日志
    if (response.config.url === '/teacher/grades' && response.config.method === 'post') {
      console.log('成绩录入完整响应:', response.data);
      console.log('响应状态码:', response.status);
      console.log('响应头:', response.headers);
    }

    const res = response.data;
    console.log('响应数据结构:', res);

    // 成功状态码判断 - 支持多种响应格式
    // 1. 标准code=200格式
    if (res.code === 200) {
      console.log('处理code=200的响应');
      
      // 移除自动消息显示，避免与组件内的消息显示重复
      // 不再自动显示res.data.message或res.message
      // 消息显示将由具体的API调用处负责
      return res;
    } 
    // 2. data.success=true格式（用于留言板更新等接口）
    else if (res.data && res.data.success === true) {
      // 显示成功消息，使用绿色提示
      if (res.data.message) {
        ElMessage({
          message: res.data.message,
          type: 'success'
        });
        console.log('操作成功:', res.data.message);
      }
      return res;
    }
    else {
      // 错误情况下也不显示红色提示，只记录日志
      console.error('操作失败:', res.message || res.data?.message || '未知错误');
      return Promise.reject(new Error(res.message || res.data?.message || 'Error'));
    }
  },
  (error) => {
    // 关闭加载动画
    if (loadingInstance) loadingInstance.close();

    // 错误处理
    if (error.response) {
      const status = error.response.status;
      
      switch (status) {
        case 401:
          // 未授权，清除token并跳转登录页
          // 避免重复清除token导致的问题
          if (authStore.token) {
            authStore.logout();
          }
          // 使用replace避免添加到历史记录，防止用户返回
          router.replace('/login');
          // 不使用ElMessage显示错误提示，避免退出登录过程中显示未登录信息
          console.error('登录已过期，请重新登录');
          break;
        case 403:
          console.error('没有权限访问');
          break;
        case 404:
          console.error('接口不存在');
          break;
        case 500:
          console.error('服务器内部错误');
          break;
        default:
          console.error(`请求错误: ${status}`);
      }
    } else if (error.message.includes('timeout')) {
      console.error('请求超时');
    } else {
      console.error('网络连接错误');
    }

    return Promise.reject(error);
  }
);

// 封装请求方法
const request = {
  // 原始get方法实现
  get(url, params = {}, config = {}) {
    return service.get(url, { params, ...config });
  },

  post(url, data = {}, config = {}) {
    return service.post(url, data, config);
  },

  put(url, data = {}, config = {}) {
    return service.put(url, data, config);
  },

  // 原始delete方法实现
  delete(url, params = {}, config = {}) {
    return service.delete(url, { params, ...config });
  },

  // 上传文件
  upload(url, file, config = {}) {
    const formData = new FormData();
    formData.append('file', file);
    return service.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...config
    });
  }
};

export default request;

